import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    FiChevronLeft, FiChevronRight, FiCalendar, FiClock,
    FiPlus, FiRefreshCw
} from "react-icons/fi";
import { MdAccessTime, MdPerson, MdKeyboardArrowDown, MdSearch, MdClose, MdContentCut, MdCalendarToday, MdSave } from "react-icons/md";

import { appointmentsAPI, usersAPI, servicesAPI, authAPI } from "services/api";

// --- BİLEŞEN İMPORTLARI ---
import AppointmentDetailModal from "./components/AppointmentDetailModal";
import QuickCreateModal from "./components/QuickCreateModal";
import BreakModal from "./components/BreakModal";

// ============================================================
// YARDIMCI FONKSİYONLAR
// ============================================================

const formatISODate = (date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
};

const extractTime = (timeStr) => {
    if (!timeStr) return "00:00";
    const timeMatch = timeStr.match(/(\d{2}:\d{2})/);
    return timeMatch ? timeMatch[1] : "00:00";
};

const timeToFloat = (timeStr) => {
    if (!timeStr) return 0;
    const safeTime = extractTime(timeStr);
    const [hours, minutes] = safeTime.split(":").map(Number);
    return (hours || 0) + (minutes || 0) / 60;
};

const addMinutesToTime = (timeStr, minutesToAdd) => {
    if (!timeStr) return "";
    const safeTime = extractTime(timeStr);
    const [hours, minutes] = safeTime.split(":").map(Number);
    const date = new Date();
    date.setHours(hours || 0, minutes || 0, 0, 0);
    date.setMinutes(date.getMinutes() + minutesToAdd);
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const doAppointmentsOverlap = (a1, a2) => {
    const start1 = timeToFloat(a1.appointment_time);
    const end1 = start1 + (a1.duration / 60);
    const start2 = timeToFloat(a2.appointment_time);
    const end2 = start2 + (a2.duration / 60);
    return Math.max(start1, start2) < Math.min(end1, end2);
};

const calculateOverlaps = (appointments) => {
    const overlapGroups = [];
    appointments.forEach(appt => {
        let placed = false;
        for (const group of overlapGroups) {
            if (group.some(existingAppt => doAppointmentsOverlap(appt, existingAppt))) {
                group.push(appt);
                placed = true;
                break;
            }
        }
        if (!placed) overlapGroups.push([appt]);
    });
    return overlapGroups;
};

// GRID AYARLARI
const START_HOUR = 8;
const END_HOUR = 23;
const HOUR_HEIGHT = 140; // Mobilde daha rahat tıklanabilmesi için yüksek tutuldu (15dk = 35px)

const timeSlots = [];
for (let h = START_HOUR; h < END_HOUR; h++) {
    for (let m = 0; m < 60; m += 15) {
        timeSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
}

// ============================================================
// ANA BİLEŞEN
// ============================================================
const AdminCalendarMobil = () => {
    const navigate = useNavigate();
    const scrollRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                navigate("/admin/calendar", { replace: true });
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [navigate]);

    // --- TEMEL STATELER ---
    const [currentDate, setCurrentDate] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [selectedStaffId, setSelectedStaffId] = useState(null);
    const [loading, setLoading] = useState(true);

    const [servicesList, setServicesList] = useState([]);

    // --- MÜŞTERİ SEÇİM STATELERİ ---
    const [isNewCustomer, setIsNewCustomer] = useState(false);
    const [customerSearch, setCustomerSearch] = useState("");
    const [filteredCustomers, setFilteredCustomers] = useState([]);

    // --- HİZMET SEÇİM STATELERİ ---
    const [serviceSearch, setServiceSearch] = useState("");
    const [showServiceDropdown, setShowServiceDropdown] = useState(false);
    const [optServiceSearch, setOptServiceSearch] = useState("");
    const [showOptServiceDropdown, setShowOptServiceDropdown] = useState(false);

    // --- MODAL STATELERİ ---
    const [quickModalOpen, setQuickModalOpen] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [molaModalOpen, setMolaModalOpen] = useState(false);
    const [selectedAppt, setSelectedAppt] = useState(null);
    const [creating, setCreating] = useState(false);

    const [molaSlotsLoading, setMolaSlotsLoading] = useState(false);
    const [molaSlotsData, setMolaSlotsData] = useState([]);

    const [quickForm, setQuickForm] = useState({
        appointment_date: formatISODate(currentDate),
        appointment_time: "",
        staff_id: "",
        service_id: "",
        optional_services: [],
        customerId: "",
        customerName: "",
        customerSurname: "",
        customerPhone: "",
        note: ""
    });

    const [molaForm, setMolaForm] = useState({
        staff_id: "",
        date: formatISODate(currentDate),
        selectedSlotIds: []
    });

    // ============================================================
    // API İSTEKLERİ
    // ============================================================
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const formattedDate = formatISODate(currentDate);

            const [apptsRes, staffRes, servRes] = await Promise.all([
                appointmentsAPI.getAll({ date: formattedDate }),
                usersAPI.getStaff(),
                servicesAPI.getAll()
            ]);

            setAppointments(apptsRes?.appointments || apptsRes?.data || []);

            const staffs = staffRes?.staff || staffRes?.data || [];
            setStaffList(staffs);

            if (staffs.length > 0 && !selectedStaffId) {
                setSelectedStaffId(staffs[0].id);
            }

            setServicesList(servRes?.services || servRes?.data || []);
        } catch (error) {
            console.error("Veri yükleme hatası:", error);
            toast.error("Takvim verileri yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!loading && scrollRef.current) {
            const now = new Date();
            if (now.toDateString() === currentDate.toDateString()) {
                const currentHour = now.getHours();
                const currentMinute = now.getMinutes();
                if (currentHour >= START_HOUR && currentHour < END_HOUR) {
                    const topPos = (currentHour - START_HOUR + currentMinute / 60) * HOUR_HEIGHT;
                    scrollRef.current.scrollTop = Math.max(0, topPos - 100);
                }
            }
        }
    }, [loading, currentDate]);

    // ============================================================
    // FİLTRELEME (KURŞUN GEÇİRMEZ TARİH VE PERSONEL FİLTRESİ)
    // ============================================================
    const currDateStr = formatISODate(currentDate);

    const displayedAppointments = useMemo(() => {
        return appointments.filter(appt => {
            if (appt.status === "cancelled" || appt.status === "rejected") return false;

            // 1. TARİH KONTROLÜ (Backend tüm tarihleri gönderse bile UI sadece bugünü gösterir)
            const apptDate = appt.appointment_date?.split('T')[0];
            if (apptDate !== currDateStr) return false;

            // 2. PERSONEL KONTROLÜ
            const sId = appt.staff_id || appt.staff?.id;
            return String(sId) === String(selectedStaffId);
        });
    }, [appointments, selectedStaffId, currDateStr]);

    const overlapGroups = useMemo(() => calculateOverlaps(displayedAppointments), [displayedAppointments]);

    const getApptStyle = (appt) => {
        const group = overlapGroups.find(g => g.includes(appt));
        let width = 100;
        let left = 0;
        let zIndex = 10;

        if (group && group.length > 1) {
            const index = group.indexOf(appt);
            width = 100 / group.length;
            left = width * index;
            zIndex = 10 + index;
        }

        const startFloat = timeToFloat(appt.appointment_time);
        const top = Math.max(0, (startFloat - START_HOUR) * HOUR_HEIGHT);
        const height = (appt.duration / 60) * HOUR_HEIGHT;

        return {
            top: `${top}px`,
            height: `${height}px`,
            left: `${left}%`,
            width: `calc(${width}% - 2px)`,
            zIndex
        };
    };

    const getStatusColors = (status) => {
        switch (status) {
            case "pending": return "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700/50";
            case "approved": return "bg-brand-100 text-brand-800 border-brand-300 dark:bg-brand-900/40 dark:text-brand-300 dark:border-brand-700/50";
            case "completed": return "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700/50";
            case "no_show": return "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700/50";
            default: return "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600";
        }
    };

    // ============================================================
    // İŞLEMLER
    // ============================================================
    const prevDay = () => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() - 1);
        setCurrentDate(d);
    };

    const nextDay = () => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() + 1);
        setCurrentDate(d);
    };

    const handleSlotClick = (time) => {
        setQuickForm(prev => ({
            ...prev,
            appointment_date: formatISODate(currentDate),
            appointment_time: time,
            staff_id: selectedStaffId || (staffList[0]?.id || ""),
            customerId: "", customerName: "", customerSurname: "", customerPhone: "",
            service_id: "", optional_services: [], note: ""
        }));
        setIsNewCustomer(false);
        setCustomerSearch("");
        setFilteredCustomers([]);
        setQuickModalOpen(true);
    };

    const handleCustomerSearch = async (e) => {
        const term = e.target.value;
        setCustomerSearch(term);
        if (term.length > 2 && !isNewCustomer) {
            try {
                const res = await usersAPI.getCustomers({ search: term });
                setFilteredCustomers(res.customers || res.data || []);
            } catch (err) {
                console.error(err);
            }
        } else {
            setFilteredCustomers([]);
        }
        setQuickForm(prev => ({ ...prev, customerName: term, customerId: "" }));
    };

    const selectCustomer = (customer) => {
        setQuickForm(prev => ({
            ...prev,
            customerId: customer.id,
            customerName: customer.name,
            customerSurname: customer.surname || "",
            customerPhone: customer.phone || ""
        }));
        setCustomerSearch(`${customer.name} ${customer.surname || ""} - ${customer.phone}`);
        setFilteredCustomers([]);
    };

    const handleQuickCreate = async (e) => {
        if (e) e.preventDefault();
        setCreating(true);

        try {
            let finalCustomerId = quickForm.customerId;

            if (isNewCustomer || !finalCustomerId) {
                if (!quickForm.customerName || !quickForm.customerPhone) {
                    toast.error("İsim ve Telefon alanları zorunludur.");
                    setCreating(false);
                    return;
                }
                let formattedPhone = quickForm.customerPhone;
                if (!formattedPhone.startsWith("+90")) {
                    formattedPhone = "+90" + formattedPhone.replace(/^0+/, "");
                }

                const newCustPayload = {
                    name: quickForm.customerName,
                    surname: quickForm.customerSurname || " ",
                    phone: formattedPhone,
                    email: `randevu-${Date.now()}@example.com`,
                    password: "password123",
                    password_confirmation: "password123",
                    role: "customer"
                };

                const res = await authAPI.register(newCustPayload);
                if (res.success || res.user) {
                    finalCustomerId = res.user?.id || res.data?.id;
                } else {
                    toast.error("Müşteri oluşturulamadı.");
                    setCreating(false);
                    return;
                }
            }

            const payload = {
                customer_id: finalCustomerId,
                staff_id: quickForm.staff_id,
                service_id: quickForm.service_id,
                optional_services: quickForm.optional_services,
                appointment_date: quickForm.appointment_date,
                appointment_time: quickForm.appointment_time,
                note: quickForm.note || "Mobil takvimden eklendi"
            };

            await appointmentsAPI.create(payload);
            toast.success("Randevu başarıyla oluşturuldu.");
            setQuickModalOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || "Kayıt işlemi başarısız.");
        } finally {
            setCreating(false);
        }
    };

    const handleCancelAppt = async () => {
        if (window.confirm("Bu randevuyu iptal etmek istediğinize emin misiniz?")) {
            try {
                await appointmentsAPI.updateStatus(selectedAppt.id, 'cancelled');
                toast.success("Randevu iptal edildi.");
                setDetailsModalOpen(false);
                fetchData();
            } catch (err) {
                toast.error("İptal edilirken bir hata oluştu.");
            }
        }
    };

    const handleNoShowAppt = async () => {
        if (window.confirm("Müşteriyi gelmedi olarak işaretlemek istiyor musunuz?")) {
            try {
                await appointmentsAPI.updateStatus(selectedAppt.id, 'no_show');
                toast.success("Gelmedi olarak işaretlendi.");
                setDetailsModalOpen(false);
                fetchData();
            } catch (err) {
                toast.error("İşlem başarısız.");
            }
        }
    };

    const handleAddToBlacklist = async () => {
        toast.info("Kara liste özelliği hazırlanıyor.");
    };

    const handleToggleBreak = async () => {
        toast.info("Mola servisi bağlanıyor...");
    };

    const handleSaveMola = async () => {
        toast.info("Mola servisi bağlanıyor...");
    };

    // ============================================================
    // RENDER EKRANI
    // ============================================================
    if (loading && appointments.length === 0 && staffList.length === 0) {
        return (
            <div className="flex flex-col h-full w-full p-4 gap-4 animate-pulse bg-gray-50 dark:bg-navy-900">
                <div className="h-20 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />
                <div className="flex-1 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[100dvh] w-full bg-white dark:bg-navy-900 overflow-hidden relative">
            <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />

            {/* 1. ÜST BAR: TARİH VE PERSONEL SEÇİMİ */}
            <div className="flex-none bg-white dark:bg-navy-800 shadow-sm border-b border-gray-100 dark:border-white/10 z-20">
                <div className="flex items-center justify-between p-4 pb-2">
                    <button onClick={prevDay} className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 dark:bg-navy-700 text-gray-600 dark:text-gray-300 hover:bg-brand-500 hover:text-white transition-all active:scale-95 border border-gray-200 dark:border-white/5">
                        <FiChevronLeft size={20} />
                    </button>

                    <div className="flex flex-col items-center">
                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-0.5">
                            {currentDate.toLocaleDateString("tr-TR", { weekday: "long" })}
                        </span>
                        <span className="text-lg font-black text-navy-700 dark:text-white flex items-center gap-2">
                            <FiCalendar className="text-brand-500" />
                            {currentDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                        </span>
                    </div>

                    <button onClick={nextDay} className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 dark:bg-navy-700 text-gray-600 dark:text-gray-300 hover:bg-brand-500 hover:text-white transition-all active:scale-95 border border-gray-200 dark:border-white/5">
                        <FiChevronRight size={20} />
                    </button>
                </div>

                <div className="flex overflow-x-auto gap-2 px-4 pb-4 custom-sidebar-scroll">
                    {staffList.map(staff => (
                        <button
                            key={staff.id}
                            onClick={() => setSelectedStaffId(staff.id)}
                            className={`flex-none px-5 py-2.5 rounded-xl text-sm font-bold transition-all border whitespace-nowrap ${
                                String(selectedStaffId) === String(staff.id)
                                    ? "bg-brand-500 border-brand-500 text-white shadow-md shadow-brand-500/30"
                                    : "bg-white dark:bg-navy-800 border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-navy-700"
                            }`}
                        >
                            {staff.name} {staff.surname}
                        </button>
                    ))}
                </div>
            </div>

            {/* 2. KAYDIRILABİLİR TAKVİM IZGARASI */}
            <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-navy-900 relative p-2" ref={scrollRef}>
                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="relative w-full bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden" style={{ height: `${(END_HOUR - START_HOUR) * HOUR_HEIGHT}px` }}>

                        {/* Saat Çizgileri */}
                        {timeSlots.map((time, index) => {
                            const isHour = time.endsWith(":00");
                            return (
                                <div
                                    key={time}
                                    className="absolute w-full flex border-t border-gray-100 dark:border-navy-700/50"
                                    style={{ top: `${index * (HOUR_HEIGHT / 4)}px`, height: `${HOUR_HEIGHT / 4}px` }}
                                >
                                    <div className="w-14 flex-none relative bg-gray-50 dark:bg-navy-900/50 border-r border-gray-100 dark:border-navy-700/50">
                                        {isHour && (
                                            <span className="absolute -top-2.5 right-2 text-[10px] font-bold text-gray-400 dark:text-gray-500">
                                                {time}
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        className={`flex-1 active:bg-brand-50 dark:active:bg-brand-900/20 transition-colors ${!isHour ? 'border-t border-dashed border-gray-100 dark:border-navy-700/30' : ''}`}
                                        onClick={() => handleSlotClick(time)}
                                    />
                                </div>
                            );
                        })}

                        {/* POINTER-EVENTS-NONE: Boşluklara tıklamayı engellememek için */}
                        <div className="absolute top-0 bottom-0 right-0 left-14 p-1 pointer-events-none">
                            {displayedAppointments.map((appt) => {
                                const style = getApptStyle(appt);
                                const statusColor = getStatusColors(appt.status);

                                return (
                                    <div
                                        key={appt.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedAppt(appt);
                                            setDetailsModalOpen(true);
                                        }}
                                        className={`absolute rounded-xl border p-2 shadow-sm cursor-pointer transition-transform active:scale-95 flex flex-col justify-start overflow-hidden pointer-events-auto ${statusColor}`}
                                        style={style}
                                    >
                                        <div className="text-[10px] font-black opacity-80 leading-none mb-0.5 flex items-center gap-1">
                                            <FiClock size={10} /> {extractTime(appt.appointment_time)}
                                        </div>
                                        <div className="text-xs font-bold leading-tight truncate drop-shadow-sm">
                                            {appt.customer?.name} {appt.customer?.surname}
                                        </div>
                                        {appt.duration >= 30 && (
                                            <div className="text-[10px] font-medium opacity-90 truncate mt-0.5">
                                                {appt.service?.name}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Yüzen Butonlar (Yenileme ve Ekleme) */}
            <div className="absolute bottom-6 right-6 flex flex-col gap-3 z-30">
                <button
                    onClick={fetchData}
                    className="h-12 w-12 rounded-full bg-white dark:bg-navy-800 text-brand-500 shadow-lg border border-gray-100 dark:border-white/10 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-navy-700 active:scale-95 transition-all"
                >
                    <FiRefreshCw size={20} className={loading ? "animate-spin" : ""} />
                </button>
                <button
                    onClick={() => handleSlotClick("09:00")}
                    className="h-14 w-14 rounded-full bg-brand-500 text-white shadow-xl shadow-brand-500/40 flex items-center justify-center hover:bg-brand-600 active:scale-95 transition-all"
                >
                    <FiPlus size={28} />
                </button>
            </div>

            {/* MODALLAR */}
            {detailsModalOpen && selectedAppt && (
                <AppointmentDetailModal
                    open={detailsModalOpen}
                    onClose={() => setDetailsModalOpen(false)}
                    appt={selectedAppt}
                    onCancel={handleCancelAppt}
                    onNoShow={handleNoShowAppt}
                    onAddToBlacklist={handleAddToBlacklist}
                />
            )}

            {quickModalOpen && (
                <QuickCreateModal
                    open={quickModalOpen}
                    onClose={() => setQuickModalOpen(false)}
                    quickForm={quickForm}
                    setQuickForm={setQuickForm}
                    servicesList={servicesList}
                    staffList={staffList}
                    availableSlots={[]}
                    slotsLoading={false}
                    isNewCustomer={isNewCustomer}
                    setIsNewCustomer={setIsNewCustomer}
                    customerSearch={customerSearch}
                    setCustomerSearch={setCustomerSearch}
                    filteredCustomers={filteredCustomers}
                    handleCustomerSearch={handleCustomerSearch}
                    selectCustomer={selectCustomer}
                    serviceSearch={serviceSearch}
                    setServiceSearch={setServiceSearch}
                    showServiceDropdown={showServiceDropdown}
                    setShowServiceDropdown={setShowServiceDropdown}
                    optServiceSearch={optServiceSearch}
                    setOptServiceSearch={setOptServiceSearch}
                    showOptServiceDropdown={showOptServiceDropdown}
                    setShowOptServiceDropdown={setShowOptServiceDropdown}
                    creating={creating}
                    onSubmit={handleQuickCreate}
                    onToggleBreak={handleToggleBreak}
                />
            )}

            {molaModalOpen && (
                <BreakModal
                    open={molaModalOpen}
                    onClose={() => setMolaModalOpen(false)}
                    staffList={staffList}
                    molaForm={molaForm}
                    setMolaForm={setMolaForm}
                    molaSlotsData={molaSlotsData}
                    molaSlotsLoading={molaSlotsLoading}
                    creating={creating}
                    onSave={handleSaveMola}
                />
            )}

            {/* Scrollbar CSS */}
            <style jsx>{`
                .custom-sidebar-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
                .custom-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
                .custom-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(163, 174, 208, 0.3); border-radius: 10px; }
                .dark .custom-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); }
            `}</style>
        </div>
    );
};

export default AdminCalendarMobil;